import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    useMaterialReactTable,
    type MRT_Row, MRT_ColumnFiltersState,
} from 'material-react-table';
import {Box, Button, IconButton, Stack, Tooltip} from '@mui/material';
import DropMonitorProgetti from './DropMonitorProgetti';
import {useGetAllMonitorProgettiRiepilogo, useGetUserProg, useSpprCreateDatiProgNodi,} from './Services';
import { MonitorProgettiRiepilogo } from './types';
import ListaMonitorProgettiNodi from './ListaMonitorProgettiNodi';
import { useTabSettings } from '../../common/Services';
import ListaMonitorProgettiNodiInline from "./ListaMonitorProgettiNodiInline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MonitorProgettiExp from "./MonitorProgettiExp";
import EditIcon from "@mui/icons-material/Edit";
import EditRicavi from "./EditRicavi";
import ListaMonitorProgettiDatiProgInline from "./ListaMonitorProgettiDatiProgInline";



const ListaMonitorProgettiRiepilogo: React.FC = () => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [isDialogMonitorProgettiNodiOpen, setIsDialogMonitorProgettiNodiOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<MonitorProgettiRiepilogo | null>(null);

  const keyusername = localStorage.getItem('keyusername') || 'NN';
  const { columnVisibility,  setColumnVisibility,  columnFilters, setColumnFilters,  sorting,  setSorting, pagination,  setPagination,  saveTabSettings,} = useTabSettings('ListaMonitorProgettiRiepilogo', keyusername);
  const [draftColumnFilters, setDraftColumnFilters] = useState<MRT_ColumnFiltersState>(columnFilters);



const [ricaviOpen, setRicaviOpen] = useState(false);
const [editingRicaviRow, setEditingRicaviRow] = useState<MonitorProgettiRiepilogo | null>(null);


  const {
    data: { results = [], count = 0 } = {},
    isError,
    isFetching,
    isLoading,
  } = useGetAllMonitorProgettiRiepilogo(pagination, columnFilters, globalFilter, sorting);

  const openMonitorProgettiNodiModal = (row: MRT_Row<MonitorProgettiRiepilogo>) => {
    setSelectedRowData(row.original);
    setIsDialogMonitorProgettiNodiOpen(true);
  };

  const keyrole = localStorage.getItem('keyrole') || '';
  const keycoddip = localStorage.getItem('coddip') || '';
  const isAdmin =  keyrole.includes('TimeReportAdmin') ||  keycoddip.includes('DS');



  const {  mutate: runSpprProcedure, isPending: isSpprLoading, data: spprData,  error: spprError,} = useSpprCreateDatiProgNodi();

  const { data: userProgs = [], isLoading: isUserProgLoading } = useGetUserProg(keyusername);

  const [isDialogDropOpen, setIsDialogDropOpen] = useState(false);

  const [isDialogExporterOpen, setIsDialogExporterOpen] = useState(false);


  const filteredResults = useMemo(() => {
      const userProgCodprogs = new Set(userProgs.map((up) => up.codprog.trim()));
      const filtered = results.filter((item) => {
        const cod = item.CODPROG?.trim();
        return !userProgCodprogs.has(cod);
      });
      return filtered;
    }, [results, userProgs]);

  const renderNumberWithHighlight = (rawValue: string) => {
    const value = parseFloat(rawValue.replace(',', '.'));
    return (
      <div
        style={{
          backgroundColor: value < 0 ? '#ffe5e5' : 'transparent',
          padding: '2px 4px',
          borderRadius: '4px',
          textAlign: 'right',
        }}
      >
        {rawValue}
      </div>
    );
  };

const columns = useMemo<MRT_ColumnDef<MonitorProgettiRiepilogo>[]>(() => {

  const baseColumns: MRT_ColumnDef<MonitorProgettiRiepilogo>[] = [
    { accessorKey: 'DESDITTA', header: 'Ditta', size: 1 },
    { accessorKey: 'CODPROG', header: 'Codice', size: 1 },
{
  accessorKey: 'INDCLIBLOC',
  header: 'StatoCli',
  size:1,
  Cell: ({ cell }) => {
    const value = cell.getValue<string>();

    return (
      <Box
        sx={{
          backgroundColor: value === 'SOSPESO' ? '#ffe5e5' : 'white',
          textAlign: 'left',
          borderRadius: '4px',
          px: 1,
        }}
      >
        {value}
      </Box>
    );
  },
},



    { accessorKey: 'REF', header: 'Ref', size: 1 },
    { accessorKey: 'DESPROG', header: 'Progetto', size: 10 },
    { accessorKey: 'STATOPROG', header: 'StatoPro', size: 1 },
    { accessorKey: 'GRUSTAT4', header: 'Gruppo', size: 1 },
    { accessorKey: 'OREORD', header: 'OreLav', size: 0.5 },
    { accessorKey: 'ORECONT', header: 'OrePrep', size: 1 },
    { accessorKey: 'OREPREV', header: 'OreStim', size: 1 },
    {
      accessorKey: 'OREDIFFPREP',
      header: 'OreDiffPrep',
      size: 1,
      Cell: ({ cell }) => renderNumberWithHighlight(cell.getValue<string>()),
    },
    {
      accessorKey: 'OREDIFFSTIMA',
      header: 'OreDiffStima',
      size: 1,
      Cell: ({ cell }) => renderNumberWithHighlight(cell.getValue<string>()),
    },
  ];

  const adminColumns: MRT_ColumnDef<MonitorProgettiRiepilogo>[] = [
    { accessorKey: 'COSTI', header: 'Costi', size: 1 },

    {
      accessorKey: 'RICAVI',
      header: 'Ricavi',
      size: 1,
      Cell: ({ row, cell }) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: '4px',
          }}
        >
          <span>{cell.getValue<string>()}</span>
          <Tooltip title="Modifica Ricavi">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditingRicaviRow(row.original);
                setRicaviOpen(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },

    {
      accessorKey: 'MARGINE',
      header: 'Margine',
      size: 1,
      Cell: ({ cell }) => renderNumberWithHighlight(cell.getValue<string>()),
    },
  ];

  return [
    ...baseColumns,
    ...(isAdmin ? adminColumns : []),
  ];

}, [isAdmin]);

  const table = useMaterialReactTable({
    columns,
    data: filteredResults,
    // data: results,
    rowCount: count,
    getRowId: (row) => (row.CODPROG ? row.CODPROG.trim() : ''),
    enableMultiRowSelection: false,
    enableRowSelection: false,
    enableSelectAll: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableEditing: false,
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableColumnActions: false,
    renderDetailPanel: ({ row }) => (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              padding: 0,
              margin: 0,
              width: '100%',
            }}
          >
            <ListaMonitorProgettiDatiProgInline codprogprop={row.original.CODPROG.trim()} />
            <ListaMonitorProgettiNodiInline codprogprop={row.original.CODPROG.trim()} />

          </Box>
        ),
    enableExpandAll: false,
    enableExpanding: true,




     muiTableBodyRowProps: ({ row }) => ({
              onClick: () => row.toggleExpanded(),
              sx: {
                cursor: 'pointer',
                backgroundColor: row.getIsExpanded() ? '#d3d3d3' : 'inherit',
                '&:hover': {
                  backgroundColor: row.getIsExpanded() ? '#a9a9a9' : '#f0f0f0',
                },
              },
            }),

    // displayColumnDefOptions: {
    //   'mrt-row-select': {
    //     header: 'Escludi',
    //   },
    // },
    initialState: {
      density: 'compact',
    },
   muiPaginationProps: {
      rowsPerPageOptions: [15, 25, 50, 100, 1000],
   },
    muiTableBodyCellProps: {
      sx: {
        padding: '4px',
        fontSize: '0.80rem',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        padding: '4px',
        fontSize: '1rem',
      },
    },
    muiTableContainerProps: {
      sx: {
        minHeight: '400px',
      },
    },
    muiToolbarAlertBannerProps: isError
      ? { color: 'error', children: 'Error loading data' }
      : undefined,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setDraftColumnFilters,
    //
    // onColumnFiltersChange: (updater) => {
    //   const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
    //   setColumnFilters(newFilters);
    //   saveTabSettings(columnVisibility, newFilters, sorting, pagination);
    // },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      saveTabSettings(columnVisibility, columnFilters, newSorting, pagination);
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
      saveTabSettings(columnVisibility, columnFilters, sorting, newPagination);
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
      saveTabSettings(newVisibility, columnFilters, sorting, pagination);
    },
    // onRowSelectionChange: handleSelectionChange,


    renderTopToolbarCustomActions: () => (
      <>
          <Stack
              direction="row"
              spacing={0}
              sx={{
                width: '60%',
                padding: '4px',
                alignItems: 'center',
              }}
            >
        <Box sx={{ width: '25%', textAlign: 'right' }}>


      <Button
        variant="contained"
        onClick={() => setIsDialogDropOpen(true)}
        sx={{ width: 160 }}
      >
        PROG. ESCLUSI
      </Button>

        {isDialogDropOpen && (
        <DropMonitorProgetti
          username={keyusername}
          open={isDialogDropOpen}
          onClose={() => setIsDialogDropOpen(false)}
          onSave={() => setIsDialogDropOpen(false)}
        />
      )}
  </Box>





              <Box sx={{ width: '25%', textAlign: 'right' }}>
                      <Button
                        variant="contained"
                        sx={{
                          width: 160,
                          backgroundColor: 'red',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#cc0000',
                          },
                        }}
                        onClick={() => runSpprProcedure()}
                        disabled={isSpprLoading}
                      >
                        {isSpprLoading ? 'LOADING...' : 'AGGIORNA DATI'}
                      </Button>
                    </Box>


              <Box sx={{width: '25%', textAlign: 'right'}}>
                  <Button
                      variant="contained"
                      onClick={() => setIsDialogExporterOpen(true)}
                      startIcon={<FileDownloadIcon/>}
                      sx={{width: 160}}
                  >
                      EXPORT DATI
                  </Button>
      {isDialogExporterOpen && (
            <MonitorProgettiExp
              pagination={pagination}
              columnFilters={columnFilters}
              globalFilter={globalFilter}
              sorting={sorting}
              columnVisibility={columnVisibility}
              columns={columns}
              onClose={() => setIsDialogExporterOpen(false)}
            />)}

    </Box>


  <Box sx={{ width: '25%', textAlign: 'right' }}>
    <Button
      variant="contained"
      onClick={() => {
        setColumnFilters(draftColumnFilters);
        saveTabSettings(columnVisibility, draftColumnFilters, sorting, pagination);
      }}
      sx={{ width: 160 }}
    >
      Applica Filtri
    </Button>
  </Box>
</Stack>

      </>
    ),



state: {

      // rowSelection: selectedRowIds,
      columnVisibility,
      isLoading,
      showProgressBars: isFetching,
      showAlertBanner: isError,
      columnFilters,
      globalFilter,
      pagination,
      sorting,
    },
  });

  return (
    <Box sx={{ mt: '60px' }}>
      <MaterialReactTable table={table} />
      {isDialogMonitorProgettiNodiOpen && selectedRowData && (
        <ListaMonitorProgettiNodi
          codprogprop={selectedRowData.CODPROG.trim()}
          desprogprop={selectedRowData.DESPROG || ''}
          onClose={() => setIsDialogMonitorProgettiNodiOpen(false)}
        />
      )}



        {editingRicaviRow && (
          <>
            <EditRicavi
              open={ricaviOpen}
              onClose={() => setRicaviOpen(false)}
              codprog={editingRicaviRow.CODPROG.trim()}
              ditta={Math.trunc(parseFloat(editingRicaviRow.CODDITTA.toString().replace(',', '.')))}
            />
          </>
        )}



    </Box>
  );
};

export default ListaMonitorProgettiRiepilogo;
